import React, { useState, useEffect, useCallback } from 'react';
import { CarpoolVehicle, FilterOptions } from './types';
import baserowApi from './services/baserowApi';
import VehicleCard from './components/VehicleCard';
import AddVehicleCard from './components/AddVehicleCard';
import AddVehicleModal from './components/AddVehicleModal';
import FilterBar from './components/FilterBar';
import VehicleFilterBar from './components/VehicleFilterBar';
import StatsOverview from './components/StatsOverview';
import { Loader2, AlertCircle, Car, RefreshCw } from 'lucide-react';
import { filterActiveVehicles, filterEndedVehicles } from './utils/contractUtils';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy
} from '@dnd-kit/sortable';
import { Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import Schadenmanagement from './components/Schadenmanagement';
import DocumentStorage from './components/DocumentStorage';
import CarPolicePage from './components/CarPolicePage';
import FahrerNutzerTable from './components/FahrerNutzerTable';
import NotificationBar from './components/NotificationBar';
import DamageCostsOverview from './components/DamageCostsOverview';
import VehicleConfigurator from './components/VehicleConfigurator';
import VehicleDetailPage from './components/VehicleDetailPage';

function MainApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<CarpoolVehicle[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<CarpoolVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterOptions>({});
  const [searchTerm, setSearchTerm] = useState('');
  // Dark Mode entfernt
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [dragMode, setDragMode] = useState(false);
  const [vehicleOrder, setVehicleOrder] = useState<number[]>([]);
  // Dummy-State für Dokumente pro Fahrzeug
  const [vehicleDocuments, setVehicleDocuments] = useState<Record<string, Record<string, string[]>>>({});
  const [archivedVehicles, setArchivedVehicles] = useState<CarpoolVehicle[]>([]);
  const [archivedVehicleDocuments, setArchivedVehicleDocuments] = useState<Record<string, Record<string, string[]>>>({});
  const [sidebarOpen, setSidebarOpen] = useState<{dokumentenablage: boolean}>({dokumentenablage: false});
  const [dokumentenablageTab, setDokumentenablageTab] = useState<'fahrzeuge' | 'archiv'>('fahrzeuge');

  useEffect(() => {
    loadVehicles();
  }, []);

  // Kein Reload bei jedem Routenwechsel – vermeidet unnötige Spinner auf Unterseiten

  // Dark Mode entfernt

  useEffect(() => {
    setVehicleOrder(filteredVehicles.map(v => v.id));
    // Drag-Modus beenden, wenn sich die Liste ändert
    setDragMode(false);
  }, [filteredVehicles]);

  // dnd-kit Sensoren
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // dnd-kit Handler
  const handleDndEnd = (event: any) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = vehicleOrder.indexOf(active.id);
      const newIndex = vehicleOrder.indexOf(over.id);
      setVehicleOrder(arrayMove(vehicleOrder, oldIndex, newIndex));
    }
    setDragMode(false);
  };

  // Funktion, die an VehicleCard übergeben wird, um Drag-Modus zu starten
  const handleStartDrag = () => setDragMode(true);
  const handleStopDrag = () => setDragMode(false);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await baserowApi.getVehicles();
      
      // Lade für jedes Fahrzeug auch die Bilder aus den Dokumenten
      const vehiclesWithImages = await Promise.all(
        response.results.map(async (vehicle: any) => {
          try {
            const documentsResponse = await baserowApi.getDocumentsForVehicle(vehicle.id);
            
            // Filtere Bilder aus den Dokumenten
            const imageDocuments = documentsResponse.results.filter((doc: any) => 
              doc.content_type && doc.content_type.startsWith('image/')
            );
            
            // Konvertiere Dokumente zu Bild-Format
            const images = imageDocuments.map((doc: any) => ({
              url: doc.file_url,
              name: doc.title,
              size: doc.files?.[0]?.size || 0,
              mime_type: doc.content_type,
              is_image: true,
              uploaded_at: doc.created_at
            }));
            
            return { ...vehicle, images };
          } catch (err) {
            console.warn(`Fehler beim Laden der Bilder für Fahrzeug ${vehicle.id}:`, err);
            return { ...vehicle, images: [] };
          }
        })
      );
      
      setVehicles(vehiclesWithImages);
    } catch (err) {
      // Detailiertere Fehlermeldungen, um Setup-Probleme schneller zu finden
      const e: any = err;
      // eslint-disable-next-line no-console
      console.error('Error loading vehicles:', e);
      let message = 'Fehler beim Laden der Fahrzeugdaten.';
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      const text = e?.message || String(e);
      if (text && String(text).includes('Baserow Token nicht konfiguriert')) {
        message = 'Baserow Token fehlt oder wird nicht geladen. In Produktion setzt der Server den Token. Für lokale Tests können Sie ihn temporär in localStorage setzen (nicht empfohlen).';
      } else if (status === 401 || status === 403) {
        message = 'Baserow: Zugriff verweigert (401/403). Token ungültig oder unzureichende Berechtigung.';
      } else if (e?.isAxiosError && !e?.response) {
        message = 'Netzwerk- oder CORS-Fehler beim Abrufen der Fahrzeugdaten. Bitte CORS/Netzwerk prüfen.';
      } else if (detail) {
        message = `Baserow-Fehler: ${detail}`;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const filterVehicles = useCallback(() => {
    // Wähle zwischen aktiven und beendeten Fahrzeugen basierend auf Filter
    let filtered = filters.showEnded ? filterEndedVehicles(vehicles) : filterActiveVehicles(vehicles);

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(vehicle =>
        vehicle.model.toLowerCase().includes(searchLower) ||
        vehicle.license_plate.toLowerCase().includes(searchLower) ||
        (vehicle.manufacturer && typeof vehicle.manufacturer === 'string'
          ? vehicle.manufacturer.toLowerCase().includes(searchLower)
          : vehicle.manufacturer && typeof vehicle.manufacturer === 'object'
          ? vehicle.manufacturer.value.toLowerCase().includes(searchLower)
          : false) ||
        (typeof vehicle.type === 'string'
          ? vehicle.type.toLowerCase().includes(searchLower)
          : vehicle.type && typeof vehicle.type === 'object'
          ? vehicle.type.value.toLowerCase().includes(searchLower)
          : false)
      );
    }

    // Apply filters
    if (filters.manufacturer) {
      filtered = filtered.filter(vehicle => {
        const vehicleManufacturer = typeof vehicle.manufacturer === 'string' 
          ? vehicle.manufacturer 
          : vehicle.manufacturer?.value;
        return vehicleManufacturer === filters.manufacturer;
      });
    }

    if (filters.type) {
      filtered = filtered.filter(vehicle => {
        const vehicleType = typeof vehicle.type === 'string' 
          ? vehicle.type 
          : vehicle.type?.value;
        return vehicleType === filters.type;
      });
    }

    if (filters.poolcar !== undefined) {
      filtered = filtered.filter(vehicle => vehicle.poolcar === filters.poolcar);
    }

    // Versteuerungs-Filter
    if (filters.taxation) {
      filtered = filtered.filter(vehicle => vehicle.taxation === filters.taxation);
    }

    setFilteredVehicles(filtered);
  }, [vehicles, searchTerm, filters]);

  useEffect(() => {
    filterVehicles();
  }, [filterVehicles]);

  const handleVehicleClick = (vehicle: CarpoolVehicle) => {
    // Navigiere zur Fahrzeug-Detailseite
    navigate(`/vehicle/${vehicle.id}`, { replace: false });
  };

  const handleAddVehicle = async (vehicle: any, files: any) => {
    try {
      setLoading(true);
      const response = await baserowApi.createVehicle(vehicle);
      console.log('Fahrzeug erstellt:', response);
      await loadVehicles(); // Liste neu laden nach Erstellung
      setAddModalOpen(false);
    } catch (err) {
      console.error('Fehler beim Erstellen des Fahrzeugs:', err);
      setError('Fehler beim Erstellen des Fahrzeugs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async (id: number) => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses Fahrzeug löschen möchten?')) {
      try {
        setLoading(true);
        await baserowApi.deleteVehicle(id);
        console.log('Fahrzeug gelöscht:', id);
        await loadVehicles(); // Liste neu laden nach Löschung
      } catch (err) {
        console.error('Fehler beim Löschen des Fahrzeugs:', err);
        setError('Fehler beim Löschen des Fahrzeugs');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditVehicle = async (id: number, data: any) => {
    try {
      setLoading(true);

      const response = await baserowApi.updateVehicle(id, data);
      console.log('Fahrzeug aktualisiert:', response);
      await loadVehicles(); // Liste neu laden nach Bearbeitung
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Fahrzeugs:', err);
      setError('Fehler beim Aktualisieren des Fahrzeugs');
    } finally {
      setLoading(false);
    }
  };

  // Hilfskomponente für dnd-kit Sortable VehicleCard
  function SortableVehicleCard({ id, vehicle, ...props }: any) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id });

    const style = {
      transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${isDragging ? 1.05 : 1})` : undefined,
      transition,
      zIndex: isDragging ? 50 : 1,
      opacity: isDragging ? 0.7 : 1,
      boxShadow: isDragging ? '0 8px 32px rgba(0,0,0,0.18)' : undefined,
      cursor: 'grab',
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <VehicleCard vehicle={vehicle} {...props} dragMode={props.dragMode} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-8 w-8 text-[#add015] mx-auto mb-4" />
          <p className="text-gray-600">Lade Fahrzeugdaten...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Fehler beim Laden</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadVehicles}
            className="bg-[#add015] text-black px-4 py-2 rounded-lg hover:bg-[#add015] hover:bg-opacity-80"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header nur auf der Startseite anzeigen, damit Unterseiten keinen doppelten Kopfbereich haben */}
      {location.pathname === '/dashboard' && (
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="grid grid-cols-3 items-center">
              <div className="justify-self-start">
                <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                  <span>Zurück zum Hub</span>
                </Link>
              </div>
              <div className="justify-self-center text-center">
                <h1 className="text-3xl font-bold text-gray-900">Fuhrpark Dashboard</h1>
                <p className="text-gray-600 mt-1">Verwaltung und Übersicht aller Fahrzeuge</p>
              </div>
              <div className="justify-self-end flex items-center space-x-4">
                <button
                  onClick={loadVehicles}
                  className="p-2 rounded-full border border-gray-300 bg-gray-100 hover:bg-gray-200 transition-colors"
                  title="Daten aktualisieren"
                >
                  <RefreshCw className="w-5 h-5 text-gray-700" />
                </button>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="w-full px-6 py-8">
        <Routes>
          <Route path="/" element={
            <div>
              {/* Stats Overview */}
              <StatsOverview vehicles={vehicles} />
              
              {/* Filter Bar */}
              <VehicleFilterBar
                filters={filters}
                onFilterChange={setFilters}
                searchTerm={searchTerm}
                onSearch={setSearchTerm}
                onAddVehicle={() => setAddModalOpen(true)}
              />

              {/* Damage Costs Overview */}
              <DamageCostsOverview vehicles={vehicles} />

              {/* Notification Bar */}
              <NotificationBar vehicles={vehicles} />

              {/* Vehicle Grid */}
              <div className="mt-8">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDndEnd}
                >
                  <SortableContext items={vehicleOrder} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {filteredVehicles.length === 0 ? (
                        <div className="col-span-full text-center py-12">
                          <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Fahrzeuge gefunden</h3>
                          <p className="text-gray-500">Versuchen Sie andere Filtereinstellungen oder fügen Sie ein neues Fahrzeug hinzu.</p>
                        </div>
                      ) : (
                        <>
                          <AddVehicleCard onAdd={() => setAddModalOpen(true)} />
                          {filteredVehicles.map((vehicle) => (
                            <SortableVehicleCard
                              key={vehicle.id}
                              id={vehicle.id}
                              vehicle={vehicle}
                              onClick={handleVehicleClick}
                              onStartDrag={handleStartDrag}
                              dragMode={dragMode}
                            />
                          ))}
                        </>
                      )}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>

              {/* Add Vehicle Modal */}
              {addModalOpen && (
                <AddVehicleModal
                  open={addModalOpen}
                  onAdd={handleAddVehicle}
                  onClose={() => setAddModalOpen(false)}
                />
              )}
            </div>
          } />
          <Route path="/fahrzeugmanagement-schaeden" element={<Schadenmanagement vehicles={vehicles} vehicleDocuments={vehicleDocuments} setVehicleDocuments={setVehicleDocuments} />} />
          {/* Redirect alte URL auf neue */}
          <Route path="/schadenmanagement" element={<Navigate to="/fahrzeugmanagement-schaeden" replace />} />
          <Route path="/dokumentenablage" element={
            <DocumentStorage
              vehicles={vehicles}
              archivedVehicles={archivedVehicles}
              vehicleDocuments={vehicleDocuments}
              archivedVehicleDocuments={archivedVehicleDocuments}
              tab={dokumentenablageTab}
              setTab={setDokumentenablageTab}
            />
          } />
          <Route path="/fahrer" element={<FahrerNutzerTable />} />
          <Route path="/carpolice" element={<CarPolicePage />} />
          <Route path="/fahrzeugkonfiguration" element={<VehicleConfigurator />} />
          <Route path="/vehicle/:id" element={<VehicleDetailPage vehicles={vehicles} onVehicleUpdate={loadVehicles} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default MainApp; 